import { skillEditorFileNamesCollide } from "./skill-editor-filenames";
import {
    deriveSkillEditorReferences,
    getSkillEditorRenameReferenceCount,
} from "./skill-editor-references";
import {
    isSkillEditorNameNormalizationRepair,
    normalizeSkillEditorText,
    requestSkillEditorRenameCommit,
    skillEditorScopesEqual,
    skillEditorFieldsFromSource,
    type SkillEditorBaselineFile,
    type SkillEditorFile,
    type SkillEditorFileField,
    type SkillEditorSource,
    type SkillEditorSourceFile,
    type SkillEditorState,
    type SkillEditorSkillField,
} from "./skill-editor-state";
import type {
    SkillEditorConflictCase,
    SkillEditorConflictFence,
    SkillEditorConflictValue,
    SkillEditorFieldConflict,
    SkillEditorFilenameCollision,
    SkillEditorLocalEditAgainstRemoteDelete,
    SkillEditorOperationReceipt,
    SkillEditorRemoteEditAgainstLocalDelete,
    SkillEditorSaveRecovery,
} from "./skill-editor-save";

export const PREFLIGHT_NON_SCOPE_SKILL_FIELDS: readonly SkillEditorSkillField[] =
    ["name", "description", "content", "isCore"];

export const PREFLIGHT_FILE_FIELDS: readonly SkillEditorFileField[] = [
    "name",
    "content",
];

const SKILL_FIELD_LABELS: Readonly<Record<SkillEditorSkillField, string>> = {
    name: "Skill name",
    description: "When to Use",
    content: "Instructions",
    scopeKind: "Scope",
    organizationId: "Organization",
    organizationGroupId: "Organization group",
    isCore: "Core skill",
};

const FILE_FIELD_LABELS: Readonly<Record<SkillEditorFileField, string>> = {
    name: "File name",
    content: "Content",
};

export const skillFieldValuesEqual = (
    field: SkillEditorSkillField,
    left: SkillEditorConflictValue,
    right: SkillEditorConflictValue
) => {
    if (
        (field === "name" || field === "description") &&
        typeof left === "string" &&
        typeof right === "string"
    ) {
        return (
            normalizeSkillEditorText(left) === normalizeSkillEditorText(right)
        );
    }
    return left === right;
};

export const fileFieldValuesEqual = (
    field: SkillEditorFileField,
    left: string,
    right: string
) =>
    field === "name"
        ? normalizeSkillEditorText(left) === normalizeSkillEditorText(right)
        : left === right;

export const skillEditorFileFieldChanged = (
    file: Pick<SkillEditorFile, "name" | "content">,
    baseline: SkillEditorBaselineFile,
    field: SkillEditorFileField
) => {
    if (field === "content") return file.content !== baseline.content;
    return (
        !fileFieldValuesEqual("name", file.name, baseline.name) ||
        isSkillEditorNameNormalizationRepair(file.name, baseline.name)
    );
};

export const captureSkillEditorSource = (
    source: SkillEditorSource
): SkillEditorSource => ({
    ...source,
    skill_files: source.skill_files.map((file) => ({ ...file })),
});

export const captureSkillEditorState = (
    state: SkillEditorState
): SkillEditorState => ({
    ...state,
    baseline: {
        skill: { ...state.baseline.skill },
        files: state.baseline.files.map((file) => ({ ...file })),
        seededServerIds: [...state.baseline.seededServerIds],
    },
    buffer: {
        skill: { ...state.buffer.skill },
        files: state.buffer.files.map((file) => ({ ...file })),
    },
    renameRequest: state.renameRequest ? { ...state.renameRequest } : null,
});

const recordSkillEditorSource = (
    state: SkillEditorState,
    source: SkillEditorSource
): SkillEditorState => {
    if (state.skillId !== source.id) return state;
    return {
        ...state,
        baseline: {
            ...state.baseline,
            skill: {
                skillId: source.id,
                ...skillEditorFieldsFromSource(source),
                updatedAt: source.updated_at,
                claudeSkillId: source.claude_skill_id ?? null,
                publishedAt: source.published_at ?? null,
            },
        },
    };
};

export const recordSkillEditorSourceFile = (
    state: SkillEditorState,
    clientKey: string,
    source: SkillEditorSourceFile
): SkillEditorState => {
    const baselineFile: SkillEditorBaselineFile = {
        clientKey,
        serverId: source.id,
        name: source.name,
        content: source.content,
        updatedAt: source.updated_at,
        displayName: source.name,
    };
    const baselineIndex = state.baseline.files.findIndex(
        (file) => file.clientKey === clientKey
    );
    const baselineFiles = [...state.baseline.files];
    if (baselineIndex === -1) {
        baselineFiles.push(baselineFile);
    } else {
        baselineFiles[baselineIndex] = baselineFile;
    }
    const rebasedState: SkillEditorState = {
        ...state,
        baseline: {
            ...state.baseline,
            files: baselineFiles,
            seededServerIds: state.baseline.seededServerIds.includes(source.id)
                ? state.baseline.seededServerIds
                : [...state.baseline.seededServerIds, source.id],
        },
        buffer: {
            ...state.buffer,
            files: state.buffer.files.map((file) =>
                file.clientKey === clientKey
                    ? {
                          ...file,
                          serverId: source.id,
                          renameOrigin: fileFieldValuesEqual(
                              "name",
                              file.name,
                              source.name
                          )
                              ? null
                              : source.name,
                          acknowledgedRename: null,
                      }
                    : file
            ),
        },
    };
    if (rebasedState.renameRequest?.clientKey !== clientKey) {
        return rebasedState;
    }
    const withoutStaleRequest = { ...rebasedState, renameRequest: null };
    const renameOrigin = withoutStaleRequest.buffer.files.find(
        (file) => file.clientKey === clientKey
    )?.renameOrigin;
    if (renameOrigin === null || renameOrigin === undefined) {
        return withoutStaleRequest;
    }
    return requestSkillEditorRenameCommit(
        withoutStaleRequest,
        clientKey,
        getSkillEditorRenameReferenceCount(
            deriveSkillEditorReferences(withoutStaleRequest),
            clientKey,
            renameOrigin
        )
    );
};

export function applySkillEditorOperationReceipt(
    state: SkillEditorState,
    receipt: SkillEditorOperationReceipt
): SkillEditorState {
    if (state.skillId !== receipt.operation.skillId) return state;
    switch (receipt.kind) {
        case "update-skill":
        case "publish":
            return recordSkillEditorSource(state, receipt.skill);
        case "delete-file":
            return {
                ...state,
                baseline: {
                    ...state.baseline,
                    files: state.baseline.files.filter(
                        (file) => file.serverId !== receipt.operation.fileId
                    ),
                    seededServerIds: state.baseline.seededServerIds.filter(
                        (serverId) => serverId !== receipt.operation.fileId
                    ),
                },
            };
        case "update-file":
        case "add-file":
            return recordSkillEditorSourceFile(
                state,
                receipt.operation.clientKey,
                receipt.file
            );
        case "snapshot":
            return state;
    }
}

export function createSkillEditorRetryBlockedRecovery(
    completedOperations: readonly SkillEditorOperationReceipt[],
    error: unknown
): Extract<SkillEditorSaveRecovery, { kind: "retry-blocked" }> {
    return {
        kind: "retry-blocked",
        completedOperations: [...completedOperations],
        error,
    };
}

export function createSkillEditorConflictFence(
    source: SkillEditorSource
): SkillEditorConflictFence {
    const files = [...source.skill_files]
        .sort((left, right) => left.id - right.id)
        .map((file) => ({
            serverId: file.id,
            fileName: file.name,
            updatedAt: file.updated_at,
        }));
    return {
        token: JSON.stringify([
            source.id,
            source.updated_at,
            source.claude_skill_id ?? null,
            source.published_at ?? null,
            files.map(({ serverId, updatedAt }) => [serverId, updatedAt]),
        ]),
        skillUpdatedAt: source.updated_at,
        files,
    };
}

export const classifySkillEditorConflicts = (
    state: SkillEditorState,
    authoritative: SkillEditorSource
): readonly SkillEditorConflictCase[] => {
    const fields: SkillEditorFieldConflict[] = [];
    const localDeletes: SkillEditorRemoteEditAgainstLocalDelete[] = [];
    const remoteDeletes: SkillEditorLocalEditAgainstRemoteDelete[] = [];
    const collisions: SkillEditorFilenameCollision[] = [];
    const remoteSkill = skillEditorFieldsFromSource(authoritative);

    const localScopeChanged = !skillEditorScopesEqual(
        state.buffer.skill,
        state.baseline.skill
    );
    const remoteScopeChanged = !skillEditorScopesEqual(
        remoteSkill,
        state.baseline.skill
    );
    if (
        localScopeChanged &&
        remoteScopeChanged &&
        !skillEditorScopesEqual(state.buffer.skill, remoteSkill)
    ) {
        const field: SkillEditorSkillField =
            state.buffer.skill.scopeKind !== remoteSkill.scopeKind
                ? "scopeKind"
                : remoteSkill.scopeKind === "organization"
                  ? "organizationId"
                  : "organizationGroupId";
        fields.push({
            scope: "skill",
            field,
            fieldLabel: SKILL_FIELD_LABELS[field],
            remoteValue: remoteSkill[field],
        });
    }

    for (const field of PREFLIGHT_NON_SCOPE_SKILL_FIELDS) {
        const baselineValue = state.baseline.skill[field];
        const localValue = state.buffer.skill[field];
        const remoteValue = remoteSkill[field];
        const localChanged = !skillFieldValuesEqual(
            field,
            localValue,
            baselineValue
        );
        const remoteChanged = !skillFieldValuesEqual(
            field,
            remoteValue,
            baselineValue
        );
        if (
            localChanged &&
            remoteChanged &&
            !skillFieldValuesEqual(field, localValue, remoteValue)
        ) {
            fields.push({
                scope: "skill",
                field,
                fieldLabel: SKILL_FIELD_LABELS[field],
                remoteValue,
            });
        }
    }

    const baselineByServerId = new Map(
        state.baseline.files.map((file) => [file.serverId, file])
    );
    const localByClientKey = new Map(
        state.buffer.files.map((file) => [file.clientKey, file])
    );
    const remoteByServerId = new Map(
        authoritative.skill_files.map((file) => [file.id, file])
    );

    for (const baseline of state.baseline.files) {
        const local = localByClientKey.get(baseline.clientKey);
        const remote = remoteByServerId.get(baseline.serverId);
        if (!remote) {
            if (!local) continue;
            const changedFields = PREFLIGHT_FILE_FIELDS.filter((field) =>
                skillEditorFileFieldChanged(local, baseline, field)
            );
            if (changedFields.length > 0) {
                remoteDeletes.push({
                    clientKey: baseline.clientKey,
                    serverId: baseline.serverId,
                    fileName: local.name,
                });
            }
            continue;
        }
        const remoteChangedFields = PREFLIGHT_FILE_FIELDS.filter((field) =>
            skillEditorFileFieldChanged(remote, baseline, field)
        );

        if (!local) {
            if (
                remote.updated_at !== baseline.updatedAt ||
                remoteChangedFields.length > 0
            ) {
                localDeletes.push({
                    clientKey: baseline.clientKey,
                    serverId: baseline.serverId,
                    fileName: remote.name,
                    remoteUpdatedAt: remote.updated_at,
                });
            }
            continue;
        }

        for (const field of PREFLIGHT_FILE_FIELDS) {
            const localChanged = skillEditorFileFieldChanged(
                local,
                baseline,
                field
            );
            const remoteChanged = skillEditorFileFieldChanged(
                remote,
                baseline,
                field
            );
            if (
                localChanged &&
                remoteChanged &&
                !fileFieldValuesEqual(field, local[field], remote[field])
            ) {
                fields.push({
                    scope: "file",
                    clientKey: baseline.clientKey,
                    serverId: baseline.serverId,
                    fileName: remote.name,
                    field,
                    fieldLabel: FILE_FIELD_LABELS[field],
                    remoteValue: remote[field],
                    remoteUpdatedAt: remote.updated_at,
                });
            }
        }
    }

    const localFiles = state.buffer.files.filter((file) => !file.isPlaceholder);
    const remoteNameCandidateIds = new Set(
        authoritative.skill_files
            .filter((file) => {
                const baseline = baselineByServerId.get(file.id);
                return (
                    baseline === undefined ||
                    skillEditorFileFieldChanged(file, baseline, "name")
                );
            })
            .map((file) => file.id)
    );

    for (const local of localFiles) {
        const localName = normalizeSkillEditorText(local.name);
        if (localName.length === 0) continue;
        for (const remote of authoritative.skill_files) {
            if (local.serverId === remote.id) continue;
            if (!remoteNameCandidateIds.has(remote.id)) continue;
            const remoteName = normalizeSkillEditorText(remote.name);
            if (remoteName.length === 0) continue;
            if (!skillEditorFileNamesCollide(localName, remoteName)) continue;
            collisions.push({
                localClientKey: local.clientKey,
                localFileName: local.name,
                remoteServerId: remote.id,
                remoteFileName: remote.name,
                remoteUpdatedAt: remote.updated_at,
            });
        }
    }

    const cases: SkillEditorConflictCase[] = [];
    if (fields.length > 0) {
        cases.push({ kind: "overlapping-edits", fields });
    }
    if (localDeletes.length > 0) {
        cases.push({
            kind: "remote-edit-against-local-delete",
            files: localDeletes,
        });
    }
    if (remoteDeletes.length > 0) {
        cases.push({
            kind: "local-edit-against-remote-delete",
            files: remoteDeletes,
        });
    }
    if (collisions.length > 0) {
        cases.push({
            kind: "concurrent-filename-or-path-collision",
            collisions,
        });
    }
    return cases;
};
